import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  PoBreadcrumb,
  PoDialogService,
  PoModalComponent,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import { Subscription, firstValueFrom, timer } from 'rxjs';
import { exhaustMap, take, tap } from 'rxjs/operators';
import { LancamentosService } from './lancamentos.service';
import { colunaExcel, parsePlanilhaRaw } from './lancamentos-planilha.parser';
import {
  LancamentoImportDetalhe,
  LancamentoImportPdfResponse,
  LancamentoImportPlanilhaResponse,
  LancamentoImportResultado,
  LancamentoImportStatus,
  PdfProcessoItem
} from './lancamentos-import.models';

type StatusProcesso = 'PROCESSANDO' | 'PRONTO' | 'PARCIAL' | 'BLOQUEADO';

interface ProcessoEmpresa {
  codigo: string;
  status: StatusProcesso;
  mensagem: string;
  atualizadoEm: Date;
}

@Component({
  selector: 'app-lancamentos',
  templateUrl: './lancamentos.component.html',
  styleUrls: ['./lancamentos.component.css'],
  standalone: false
})
export class LancamentosComponent implements OnInit, OnDestroy {
  @ViewChild('modalPlanilha') modalPlanilha!: PoModalComponent;
  @ViewChild('uploadPdf') uploadPdf!: PoUploadComponent;

  /** 'comum' = planilha | 'darf' = PDF e-CAC */
  tipoLancamento: 'comum' | 'darf' = 'comum';
  readonly tipoLancamentoOptions = [
    { label: 'Lançamento comum (planilha)', value: 'comum' },
    { label: 'DARF / e-CAC (PDF)', value: 'darf' }
  ];

  arquivoNome = '';
  arquivoPlanilha: File | null = null;
  codigoEmpresaOpcional: number | null = null;
  usuarioOpcional = '';
  contaCreditoOpcional: number | null = null;
  loading = false;
  loadingPdf = false;
  parsingPlanilha = false;
  planilhaCarregada = false;
  planilhaGrid: string[][] = [];
  colunasExcel: string[] = [];

  showProgress = false;
  processoStatus: StatusProcesso = 'PROCESSANDO';
  processoMensagem = '';
  inicioProcessamento: Date | null = null;
  fimProcessamento: Date | null = null;
  processosEmpresas: ProcessoEmpresa[] = [];
  requestIdAtual = '';

  showProgressPdf = false;
  processosPdf: PdfProcessoItem[] = [];
  inicioProcessamentoPdf: Date | null = null;
  fimProcessamentoPdf: Date | null = null;

  readonly pdfRestrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.pdf'],
    maxFileSize: 52428800
  };
  readonly urlPlaceholder = 'https://po-ui.io';

  private pollSub?: Subscription;
  private readonly pollIntervalMs = 3000;
  private readonly pollMaxTentativas = 100;

  readonly modeloColunas = [
    'CodigoEmpresa',
    'CodigoLote',
    'NumeroLancamento',
    'Data',
    'Debitar',
    'Creditar',
    'Valor',
    'Histórico',
    'Usuario'
  ];

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Ferramentas', link: '/contabil-outros' },
      { label: 'Lançamentos' }
    ]
  };

  constructor(
    private readonly lancamentosService: LancamentosService,
    private readonly poDialogService: PoDialogService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.pararPolling();
  }

  importarLancamentos(): void {
    if (this.loading) return;

    if (!this.arquivoPlanilha) {
      this.poNotificationService.warning('Selecione uma planilha para importar os lançamentos.');
      return;
    }

    this.poDialogService.confirm({
      title: 'Confirmar importação',
      message: `Tem certeza que deseja importar os lançamentos do arquivo "${this.arquivoNome}"? Esta ação não pode ser desfeita.`,
      confirm: () => this.executarImportacao()
    });
  }

  importarPdfs(): void {
    if (this.loadingPdf) return;

    const files = this.uploadPdf?.currentFiles ?? [];
    if (!files.length) {
      this.poNotificationService.warning('Selecione ao menos um PDF de pagamento e-CAC.');
      return;
    }

    const nomes = files.map((f: any) => f.name).join(', ');
    this.poDialogService.confirm({
      title: 'Confirmar importação de PDFs',
      message: `Enviar ${files.length} PDF(s) para lançamento contábil?\n\n${nomes}`,
      confirm: () => void this.executarImportacaoPdfs(files)
    });
  }

  visualizarPlanilha(): void {
    if (!this.planilhaCarregada || this.planilhaGrid.length === 0) {
      this.poNotificationService.warning('Nenhum conteúdo disponível para visualizar.');
      return;
    }
    this.modalPlanilha.open();
  }

  fecharVisualizacaoPlanilha(): void {
    this.modalPlanilha.close();
  }

  getStatusLabelPdf(status: PdfProcessoItem['status']): string {
    if (status === 'PRONTO') return 'Enviado';
    if (status === 'BLOQUEADO') return 'Erro';
    return 'Processando';
  }

  private async executarImportacaoPdfs(files: any[]): Promise<void> {
    this.loadingPdf = true;
    this.showProgressPdf = true;
    this.inicioProcessamentoPdf = new Date();
    this.fimProcessamentoPdf = null;
    this.processosPdf = files.map((f: any) => ({
      nomeArquivo: f.name,
      status: 'PROCESSANDO' as const,
      mensagem: 'Aguardando envio...'
    }));

    let ok = 0;
    let falhas = 0;

    for (let i = 0; i < files.length; i++) {
      const fileMeta = files[i];
      const rawFile: File | undefined = fileMeta.rawFile;
      const item = this.processosPdf[i];

      if (!rawFile) {
        item.status = 'BLOQUEADO';
        item.mensagem = 'Arquivo inválido.';
        falhas++;
        continue;
      }

      item.mensagem = 'Enviando PDF...';

      try {
        const response = await firstValueFrom(
          this.lancamentosService.importarPorPdf(rawFile, {
            contaCredito: this.contaCreditoOpcional,
            usuario: this.usuarioOpcional.trim() || null
          })
        );
        this.aplicarResultadoPdf(item, response);
        if (item.status === 'PRONTO') {
          ok++;
          fileMeta.status = 0;
        } else {
          falhas++;
          fileMeta.status = 2;
        }
      } catch (err: any) {
        item.status = 'BLOQUEADO';
        item.mensagem =
          err?.error?.error ||
          err?.error?.message ||
          err?.message ||
          'Erro ao enviar o PDF.';
        falhas++;
        fileMeta.status = 2;
      }
    }

    this.fimProcessamentoPdf = new Date();
    this.loadingPdf = false;

    if (falhas === 0) {
      this.poNotificationService.success(
        `${ok} PDF(s) enviado(s) para lançamento com sucesso.`
      );
      this.uploadPdf?.clear();
    } else if (ok === 0) {
      this.poNotificationService.error('Nenhum PDF foi enviado com sucesso. Verifique os detalhes.');
    } else {
      this.poNotificationService.warning(
        `${ok} enviado(s) e ${falhas} com erro. Verifique os detalhes abaixo.`
      );
    }
  }

  private aplicarResultadoPdf(item: PdfProcessoItem, response: LancamentoImportPdfResponse): void {
    item.cnpj = response.cnpj ?? undefined;
    item.razaoSocial = response.razaoSocial ?? undefined;
    item.tipo = response.tipo ?? undefined;
    item.valorTotal = response.valorTotal ?? undefined;
    item.requestId = response.requestId ?? undefined;
    item.totalEnviados = response.totalEnviados ?? 0;

    const erro = String(response.error || '').trim();
    if (erro || !(response.totalEnviados && response.totalEnviados > 0)) {
      item.status = 'BLOQUEADO';
      item.mensagem = erro || 'PDF processado, mas nenhum lançamento foi enviado.';
      return;
    }

    item.status = 'PRONTO';
    const empresa = response.razaoSocial || response.cnpj || 'empresa';
    item.mensagem =
      `${response.totalEnviados} lançamento(s) enfileirado(s) — ${empresa}` +
      (response.tipo ? ` (${response.tipo})` : '') +
      (response.valorTotal != null ? ` — R$ ${response.valorTotal}` : '');
  }

  private executarImportacao(): void {
    if (!this.arquivoPlanilha || this.loading) return;

    this.pararPolling();
    this.showProgress = true;
    this.processoStatus = 'PROCESSANDO';
    this.processoMensagem = 'Enviando planilha...';
    this.inicioProcessamento = new Date();
    this.fimProcessamento = null;
    this.processosEmpresas = [];
    this.requestIdAtual = '';

    this.loading = true;
    this.lancamentosService
      .importarPorPlanilha(this.arquivoPlanilha, {
        codigoEmpresa: this.codigoEmpresaOpcional,
        usuario: this.usuarioOpcional.trim() || null
      })
      .subscribe({
        next: (response) => this.tratarRespostaUpload(response),
        error: (err: any) => this.tratarErroHttp(err, 'Erro ao enviar a planilha para importação.')
      });
  }

  private tratarRespostaUpload(response: LancamentoImportPlanilhaResponse): void {
    const erroUpload = response?.error?.trim();
    if (erroUpload) {
      this.finalizarCarregamento();
      this.processoStatus = 'BLOQUEADO';
      this.fimProcessamento = new Date();
      this.processoMensagem = erroUpload;
      this.poNotificationService.error(erroUpload);
      return;
    }

    const requestId = String(response?.requestId || '').trim();
    if (!requestId) {
      this.finalizarCarregamento();
      this.processoStatus = 'BLOQUEADO';
      this.fimProcessamento = new Date();
      this.processoMensagem = 'Não foi possível iniciar a importação. Tente novamente.';
      this.poNotificationService.error(this.processoMensagem);
      return;
    }

    this.requestIdAtual = requestId;
    const total = response.totalEnviados ?? 0;
    this.processoMensagem =
      total > 0
        ? `Planilha enviada (${total} lançamento(s)). Processando...`
        : 'Planilha enviada. Processando lançamentos...';

    this.iniciarPollingResultado(requestId);
  }

  private iniciarPollingResultado(requestId: string): void {
    this.pararPolling();

    this.pollSub = timer(0, this.pollIntervalMs)
      .pipe(
        take(this.pollMaxTentativas),
        exhaustMap(() => this.lancamentosService.obterImportResultado(requestId)),
        tap((resultado) => this.aplicarResultado(resultado))
      )
      .subscribe({
        next: (resultado) => {
          if (this.isResultadoTerminal(resultado)) {
            this.finalizarCarregamento();
            this.notificarResultadoFinal(resultado);
            this.pararPolling();
          }
        },
        error: (err: any) => {
          this.finalizarCarregamento();
          this.tratarErroHttp(err, 'Erro ao consultar o resultado da importação.');
          this.pararPolling();
        },
        complete: () => {
          if (this.loading) {
            this.finalizarCarregamento();
            this.processoStatus = 'BLOQUEADO';
            this.fimProcessamento = new Date();
            this.processoMensagem =
              'O processamento está demorando. Aguarde alguns instantes e tente importar novamente.';
            this.poNotificationService.warning(this.processoMensagem);
            this.pararPolling();
          }
        }
      });
  }

  private aplicarResultado(resultado: LancamentoImportResultado): void {
    const statusApi = this.normalizarStatusApi(resultado);
    this.processoStatus = this.mapearStatusUi(statusApi);
    this.processoMensagem = this.extrairMensagemResultado(resultado, statusApi);
    this.preencherDetalhesResultado(resultado, statusApi);

    if (statusApi !== 'PENDING') {
      this.fimProcessamento = new Date();
      this.finalizarCarregamento();
    }
  }

  private isResultadoTerminal(resultado: LancamentoImportResultado): boolean {
    return this.normalizarStatusApi(resultado) !== 'PENDING';
  }

  private finalizarCarregamento(): void {
    this.loading = false;
  }

  private notificarResultadoFinal(resultado: LancamentoImportResultado): void {
    const status = this.normalizarStatusApi(resultado);
    if (status === 'SUCCESS') {
      this.poNotificationService.success('Lançamentos importados com sucesso.');
      return;
    }
    if (status === 'PARTIAL') {
      this.poNotificationService.warning(
        this.processoMensagem || 'Parte dos lançamentos foi importada. Verifique os detalhes na tela.'
      );
      return;
    }
    this.poNotificationService.error(
      this.processoMensagem || 'As alterações na base de dados não foram realizadas.'
    );
  }

  private preencherDetalhesResultado(
    resultado: LancamentoImportResultado,
    statusApi: LancamentoImportStatus
  ): void {
    const agora = new Date();

    if (this.processoStatus === 'PRONTO' || statusApi === 'PENDING' || statusApi === 'SUCCESS') {
      this.processosEmpresas = [];
      return;
    }

    const detalhes = this.extrairListaDetalhes(resultado);

    if (detalhes.length > 0) {
      this.processosEmpresas = detalhes.map((item) => ({
        codigo: this.rotuloDetalhe(item),
        status: this.statusDetalheParaUi(item, statusApi),
        mensagem: this.mensagemDetalhe(item),
        atualizadoEm: agora
      }));
      return;
    }

    const inseridos = resultado.totalInseridos;
    const falhas = resultado.totalFalhas ?? resultado.totalComErro;
    const extras: string[] = [];
    if (inseridos != null) extras.push(`${inseridos} inserido(s)`);
    if (falhas != null) extras.push(`${falhas} com falha`);

    this.processosEmpresas = [{
      codigo: 'Resumo',
      status: this.mapearStatusUi(statusApi),
      mensagem:
        extras.length > 0
          ? `${this.processoMensagem} (${extras.join(', ')}).`
          : this.processoMensagem,
      atualizadoEm: agora
    }];
  }

  private extrairListaDetalhes(resultado: LancamentoImportResultado): LancamentoImportDetalhe[] {
    const bruto = [
      ...(resultado.detalhes ?? []),
      ...(resultado.itens ?? []),
      ...(resultado.falhas ?? []),
      ...this.errosParaDetalhes(resultado.erros)
    ];
    return bruto.filter((item) => !!item.erro || !!this.mensagemDetalhe(item));
  }

  private errosParaDetalhes(erros: LancamentoImportResultado['erros']): LancamentoImportDetalhe[] {
    if (!erros?.length) return [];
    return erros.map((e) =>
      typeof e === 'string' ? { mensagem: e } : e
    );
  }

  private rotuloDetalhe(item: LancamentoImportDetalhe): string {
    const codigo = item.codigoEmpresa ?? item.codigo;
    if (codigo) return String(codigo);
    const linha = item.linha ?? item.numeroLinha;
    if (linha != null) return `Linha ${linha}`;
    return 'Registro';
  }

  private mensagemDetalhe(item: LancamentoImportDetalhe): string {
    return this.sanitizarMensagemUsuario(
      String(item.mensagem ?? item.message ?? item.erro ?? '')
    );
  }

  private statusDetalheParaUi(
    _item: LancamentoImportDetalhe,
    statusApi: LancamentoImportStatus
  ): StatusProcesso {
    if (statusApi === 'SUCCESS') return 'PRONTO';
    if (statusApi === 'PARTIAL') return 'PARCIAL';
    if (statusApi === 'ERROR') return 'BLOQUEADO';
    return 'PROCESSANDO';
  }

  private mapearStatusUi(status: LancamentoImportStatus): StatusProcesso {
    if (status === 'SUCCESS') return 'PRONTO';
    if (status === 'PARTIAL') return 'PARCIAL';
    if (status === 'ERROR') return 'BLOQUEADO';
    return 'PROCESSANDO';
  }

  private normalizarStatusApi(resultado: LancamentoImportResultado): LancamentoImportStatus {
    const s = String(resultado?.status || 'PENDING').toUpperCase();
    if (s === 'NOT_FOUND' || s === 'NAO_ENCONTRADO' || s === 'NÃO_ENCONTRADO') return 'NOT_FOUND';
    if (s === 'SUCCESS' || s === 'OK' || s === 'CONCLUIDO' || s === 'CONCLUÍDO') return 'SUCCESS';
    if (s === 'PARTIAL' || s === 'PARCIAL' || s === 'WARNING') return 'PARTIAL';
    if (
      s === 'ERROR' ||
      s === 'ERRO' ||
      s === 'FAILED' ||
      s === 'FAIL' ||
      s === 'ROLLBACK' ||
      s === 'FALHA'
    ) {
      return 'ERROR';
    }

    const inseridos = resultado.totalInseridos ?? 0;
    const falhas = resultado.totalFalhas ?? 0;
    if (inseridos > 0 && falhas === 0) return 'SUCCESS';
    if (inseridos > 0 && falhas > 0) return 'PARTIAL';
    if (falhas > 0) return 'ERROR';

    return 'PENDING';
  }

  private extrairMensagemResultado(
    resultado: LancamentoImportResultado,
    status: LancamentoImportStatus
  ): string {
    const textoApi = this.sanitizarMensagemUsuario(
      resultado.mensagem ?? resultado.message ?? resultado.error ?? ''
    );

    if (status === 'PENDING') {
      return textoApi || 'Processando lançamentos...';
    }
    if (status === 'SUCCESS') {
      return textoApi || 'Lançamentos importados com sucesso.';
    }
    if (status === 'PARTIAL') {
      return textoApi || 'Parte dos lançamentos foi importada. Verifique os detalhes abaixo.';
    }

    const prefixoErro = 'As alterações na base de dados não foram realizadas.';
    if (textoApi) {
      return textoApi.toLowerCase().includes('base de dados')
        ? textoApi
        : `${prefixoErro} ${textoApi}`;
    }
    return prefixoErro;
  }

  private sanitizarMensagemUsuario(texto: string): string {
    return String(texto || '')
      .replace(/\brollback\b/gi, '')
      .replace(/\bconector\b/gi, '')
      .replace(/\bfila\b/gi, '')
      .replace(/\btransa[cç][aã]o\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private tratarErroHttp(err: any, fallback: string): void {
    this.finalizarCarregamento();
    this.processoStatus = 'BLOQUEADO';
    this.fimProcessamento = new Date();

    if (err?.status === 404) {
      this.processoMensagem =
        'Resultado da importação não encontrado (404). Verifique se a API Ecco está em execução na mesma instância em que você enviou a planilha e tente importar novamente.';
    } else {
      this.processoMensagem =
        err?.error?.error || err?.error?.message || err?.message || fallback;
    }

    this.poNotificationService.error(this.processoMensagem || fallback);
  }

  private pararPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  onArquivoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.processarArquivo(file);
  }

  onDropArquivo(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.processarArquivo(file);
  }

  onDragOverArquivo(event: DragEvent): void {
    event.preventDefault();
  }

  limparArquivo(): void {
    this.arquivoNome = '';
    this.arquivoPlanilha = null;
    this.planilhaGrid = [];
    this.colunasExcel = [];
    this.planilhaCarregada = false;
    this.modalPlanilha?.close();
  }

  getStatusLabel(): string {
    if (this.processoStatus === 'PRONTO') return 'Sucesso';
    if (this.processoStatus === 'PARCIAL') return 'Parcial';
    if (this.processoStatus === 'BLOQUEADO') return 'Erro';
    return 'Processando';
  }

  getStatusLabelEmpresa(status: StatusProcesso): string {
    if (status === 'PRONTO') return 'Ok';
    if (status === 'PARCIAL') return 'Parcial';
    if (status === 'BLOQUEADO') return 'Falha';
    return 'Processando';
  }

  private processarArquivo(file: File): void {
    const extensaoValida = /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!extensaoValida) {
      this.poNotificationService.warning('Envie uma planilha .csv, .xlsx ou .xls.');
      return;
    }

    this.arquivoPlanilha = file;
    this.arquivoNome = file.name;
    this.planilhaGrid = [];
    this.colunasExcel = [];
    this.planilhaCarregada = false;
    this.carregarPreviewPlanilha(file);
  }

  private carregarPreviewPlanilha(file: File): void {
    this.parsingPlanilha = true;
    parsePlanilhaRaw(file)
      .then((rows) => {
        this.planilhaGrid = rows;
        const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
        this.colunasExcel = Array.from({ length: colCount }, (_, i) => colunaExcel(i));
        this.planilhaCarregada = rows.length > 0;

        if (!this.planilhaCarregada) {
          this.poNotificationService.warning('A planilha está vazia ou não pôde ser lida.');
          return;
        }

        this.poNotificationService.success(
          'Planilha carregada. Use "Visualizar planilha" para conferir o conteúdo antes de importar.'
        );
      })
      .catch(() => {
        this.planilhaGrid = [];
        this.colunasExcel = [];
        this.planilhaCarregada = false;
        this.poNotificationService.error('Não foi possível ler a planilha. Verifique o formato do arquivo.');
      })
      .finally(() => {
        this.parsingPlanilha = false;
      });
  }
}
